import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import cx from "classnames";
import yall from "yall-js";

import {
  ReactComponentStaticProps,
  ReactDefaultPropsWithChildren,
  ReactPropsWithRequiredChildren,
} from "../../types/react";

import styles from "./Image.scss";

const ImageVariants = ["default", "lazyLoading", "lazyLoadingYall"] as const;

export type ImageVariantValue = typeof ImageVariants[number];

export type ImageBaseProps<Tag extends React.ElementType> = {
  tag?: Tag;
  variant?: ImageVariantValue;
  placeholder?: string | [number, number];
  customClass?: string;
  isBackground?: boolean;
};

export const ImageDefaultTagName = "img";
export type ImageDefaultTag = typeof ImageDefaultTagName;

export type ImageProps<
  Tag extends React.ElementType = ImageDefaultTag
> = ImageBaseProps<Tag> &
  Omit<React.ComponentPropsWithRef<Tag>, keyof ImageBaseProps<Tag>>;

const defaultProps: ReactDefaultPropsWithChildren<ImageBaseProps<
  ImageDefaultTag
>> = {
  tag: ImageDefaultTagName,
  variant: "default",
  customClass: "",
  children: null,
  placeholder: "",
  isBackground: false,
};

declare global {
  interface Window {
    lazyLoadingInitialized: boolean;
  }
}

type ImageWithRefProps<Tag extends React.ElementType> = Omit<
  ImageProps<Tag>,
  "ref"
>;

const loadedImages: string[] = [];

function initYall() {
  if (window.lazyLoadingInitialized) {
    return;
  }
  if (typeof NodeList !== "undefined" && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = (Array.prototype
      .forEach as unknown) as typeof NodeList.prototype.forEach;
  }
  yall({
    observeChanges: true,
    mutationObserverOptions: {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    },
    events: {
      load(event) {
        const imgElement = event.target as HTMLImageElement;
        if (!imgElement.classList.contains("lazy")) {
          loadedImages.push(imgElement.src);
          if (
            imgElement.parentElement &&
            imgElement.parentElement.tagName === "PICTURE"
          ) {
            imgElement.parentElement
              .querySelectorAll("source")
              .forEach((sourceElement) => {
                loadedImages.push(sourceElement.srcset);
              });
          }
        }
      },
    },
  });
  window.lazyLoadingInitialized = true;
}

function imageAlreadyLoaded(tag: string, src: string): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  const img = document.querySelectorAll(`${tag}[src="${src}"]`)[0];
  if (img && (!img.parentElement || img.parentElement.tagName !== "NOSCRIPT")) {
    return true;
  }
  const imageLoaded = loadedImages.findIndex((item) => item === src) >= 0;
  return imageLoaded;
}

const HeadPortal = ({
  children: headChildren,
}: ReactPropsWithRequiredChildren) => {
  if (typeof document !== "undefined" && document.head)
    return ReactDOM.createPortal(headChildren, document.head);
  return <>{headChildren}</>;
};

HeadPortal.displayName = "HeadPortal";

HeadPortal.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

const ImageWithRef = <Tag extends React.ElementType>(
  {
    children = defaultProps.children,
    tag = defaultProps.tag,
    variant = defaultProps.variant,
    customClass = defaultProps.customClass,
    isBackground = defaultProps.isBackground,
    placeholder,
    ...tagProps
  }: ImageWithRefProps<Tag>,
  ref: React.ComponentPropsWithRef<Tag>["ref"]
): JSX.Element => {
  const CustomTag = tag;
  const tagRef = useRef<Tag | null>(null);
  const tagElement = ((tagRef.current &&
    tagRef.current) as unknown) as HTMLElement;
  const { src, srcSet, sizes, ...tagRestProps } = tagProps;
  const placeholderSrc =
    (typeof placeholder === "string" && placeholder) ||
    (Array.isArray(placeholder) &&
      `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="${placeholder[0]}" height="${placeholder[1]}" viewBox="0 0 ${placeholder[0]} ${placeholder[1]}"></svg>`) ||
    "#";
  const lazyVariant =
    variant === "lazyLoading" || variant === "lazyLoadingYall";
  const srcAttrName = src ? "src" : "srcSet";
  const lazyLoading =
    lazyVariant &&
    (isBackground ||
      !tagRef.current ||
      tagElement.getAttribute(srcAttrName) === placeholderSrc) &&
    !imageAlreadyLoaded(tag, src || srcSet);
  const lazyImgProps = {
    ...tagRestProps,
    ...(!isBackground
      ? {
          src: undefined,
          srcSet: undefined,
          [srcAttrName]: placeholderSrc,
          "data-src": src,
          "data-srcset": srcSet,
        }
      : {}),
  };
  const noLazyImgProps = isBackground ? tagRestProps : tagProps;
  const imgProps = lazyLoading ? lazyImgProps : noLazyImgProps;
  const [noJs, setNoJs] = useState(true);
  const [withLazyClass, setWithLazyClass] = useState(false);
  useEffect(() => setNoJs(false), []);
  useEffect(() => setWithLazyClass(lazyLoading), []);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (lazyLoading) {
      initYall();
    }
  }, [withLazyClass]);

  const setRef = (element: Tag) => {
    tagRef.current = element;
    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      // eslint-disable-next-line no-param-reassign
      ref.current = element;
    }
  };

  const classNames =
    tag === "source"
      ? ""
      : cx(
          {
            [styles.image]: !isBackground,
            [styles.noJs]: noJs && lazyLoading,
            lazy: !isBackground && withLazyClass,
            "lazy-bg": isBackground && withLazyClass,
          },
          customClass
        );

  const BackgroundStyle = (props: {
    id: string;
    selector: string;
    imgSrc?: string;
    imgSrcSet?: string;
    imgSizes?: string;
    inline?: boolean;
  }) => {
    const { id, selector, imgSrc, imgSrcSet, imgSizes, inline } = props;
    const head = typeof document !== "undefined" && document.head;
    const css = [];
    if (imgSrc) {
      css.push(`
        ${selector} {
          background-image: url('${imgSrc}');
        }
      `);
    }
    if (imgSrcSet && imgSizes) {
      css.push(
        imgSizes
          .trim()
          .split(",")
          .reverse()
          .map((rawSizeDef: string) => {
            const sizeDef = rawSizeDef.trim();
            const imgMap = imgSrcSet
              .split(",")
              .reduce((mapped: { [index: string]: string }, srcDef: string) => {
                const [url, size] = srcDef.trim().split(" ");
                return {
                  ...mapped,
                  [size]: url,
                };
              }, {});
            const [, mq, imgSize] = sizeDef.match(/(\([^)]+\)) ([^ ]+)/) || [];
            if (mq && imgSize) {
              const imgUrl = imgMap[imgSize];
              if (!imgUrl) {
                return "";
              }
              return `
                @media ${mq} {
                  ${selector} {
                    background-image: url('${imgUrl}');
                  }
                }
              `;
            }
            const imgUrl = imgMap[sizeDef];
            return `
              ${selector} {
                background-image: url('${imgUrl}');
              }
            `;
          })
          .join("\n")
      );
    }
    if (inline || !head) {
      return (
        /* eslint-disable react/no-danger */
        <style
          dangerouslySetInnerHTML={{
            __html: css.join("\n"),
          }}
        />
        /* eslint-enable react/no-danger */
      );
    }
    const style =
      document.querySelector(`style#${id}`) || document.createElement("style");
    style.id = id;
    style.innerHTML = css.join("\n");
    if (!style.parentElement) {
      head.append(style);
    }
    return null;
  };

  BackgroundStyle.defaultProps = {
    imgSrc: src,
    imgSrcSet: srcSet,
    imgSizes: sizes,
    inline: false,
  };

  return (
    <>
      {isBackground && !lazyLoading && (
        <BackgroundStyle
          id={`${customClass}-bg-noll`}
          selector={`.${customClass}`}
        />
      )}
      {isBackground && lazyLoading && (
        <>
          {placeholderSrc && placeholderSrc !== "#" && (
            <BackgroundStyle
              id={`${customClass}-bg-ll-ph`}
              selector={`.${customClass}`}
              imgSrc={placeholderSrc}
              imgSrcSet=""
            />
          )}
          <BackgroundStyle
            id={`${customClass}-bg-ll`}
            selector={`.${customClass}.lazy-bg-loaded`}
          />
          {noJs && (
            <noscript>
              <BackgroundStyle
                id={`${customClass}-bg-ll-ns`}
                selector={`.${customClass}.${styles.noJs}`}
                inline
              />
            </noscript>
          )}
        </>
      )}
      <CustomTag ref={setRef} className={classNames} {...imgProps}>
        {children}
      </CustomTag>
      {noJs && lazyLoading && !isBackground && (
        <noscript>
          <CustomTag className={cx(styles.image, customClass)} {...tagProps}>
            {children}
          </CustomTag>
        </noscript>
      )}
    </>
  );
};

export const Image: (<Tag extends React.ElementType = ImageDefaultTag>(
  props: ImageProps<Tag>
) => JSX.Element) &
  ReactComponentStaticProps<ImageProps, "tag"> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.forwardRef(ImageWithRef) as any;

Image.displayName = "Image";

export const ImagePropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  tag: PropTypes.string,
  variant: PropTypes.oneOf(ImageVariants),
  customClass: PropTypes.string,
};

Image.propTypes = ImagePropTypes;

Image.defaultProps = defaultProps;

export default Image;

export const BackgroundImageDefaultTagName = "div";
export type BackgroundImageDefaultTag = typeof BackgroundImageDefaultTagName;

export type BackgroundImageProps<
  Tag extends React.ElementType = BackgroundImageDefaultTag
> = Omit<ImageBaseProps<Tag>, "customClass"> & {
  customCLass: string;
} & Omit<React.ComponentPropsWithRef<Tag>, keyof ImageBaseProps<Tag>>;

export const BackgroundImage: (<
  Tag extends React.ElementType = BackgroundImageDefaultTag
>(
  props: ImageProps<Tag>
) => JSX.Element) &
  ReactComponentStaticProps<ImageProps, "tag"> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.forwardRef(ImageWithRef) as any;

BackgroundImage.displayName = "BackgroundImage";

BackgroundImage.propTypes = {
  ...ImagePropTypes,
  customClass: PropTypes.string.isRequired,
};

const {
  customClass: removeCustomClass,
  ...defaultPropsWithoutCustomCLass
} = defaultProps;

BackgroundImage.defaultProps = {
  ...defaultPropsWithoutCustomCLass,
  tag: BackgroundImageDefaultTagName,
  isBackground: true,
};
